/**
 * Property List Component
 * Displays properties grouped by category
 */

import React from 'react';
import { usePropertiesStore } from '@/store/propertiesStore';

export function PropertyList() {
  const {
    propertiesGrouped,
    searchFilter,
    expandedCategories,
    showHiddenProperties,
    toggleCategory
  } = usePropertiesStore();

  // Filter properties based on search
  const filteredGroups = new Map<string, any[]>();
  
  propertiesGrouped.forEach((props, category) => {
    const filtered = props.filter(prop => {
      // Filter hidden properties
      if (!showHiddenProperties && prop.hidden) return false;
      
      // Filter by search text
      if (searchFilter) {
        const search = searchFilter.toLowerCase();
        return (
          prop.displayName.toLowerCase().includes(search) ||
          String(prop.displayValue).toLowerCase().includes(search)
        );
      }
      return true;
    });

    if (filtered.length > 0) {
      filteredGroups.set(category, filtered);
    }
  });

  if (filteredGroups.size === 0) {
    return (
      <div className="text-center py-4 text-gray-500">
        <p>No properties found</p>
        {searchFilter && (
          <p className="text-xs mt-1">Try adjusting your search filter</p>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-2 max-h-[600px] overflow-y-auto">
      {Array.from(filteredGroups.entries()).map(([category, props]) => {
        const isExpanded = expandedCategories.has(category);
        
        return (
          <div
            key={category}
            className="border border-gray-700 rounded-lg bg-dark-200"
          >
            <button
              className="w-full px-3 py-2 flex items-center justify-between hover:bg-dark-300 transition-colors"
              onClick={() => toggleCategory(category)}
            >
              <div className="flex items-center gap-2">
                <span className="text-gray-400">
                  {isExpanded ? '▼' : '▶'}
                </span>
                <span className="font-medium text-gray-100">{category}</span>
                <span className="text-xs text-gray-500">
                  ({props.length})
                </span>
              </div>
            </button>

            {isExpanded && (
              <div className="px-3 pb-3">
                <table className="w-full text-sm">
                  <tbody>
                    {props.map((prop, index) => (
                      <tr
                        key={`${prop.displayName}-${index}`}
                        className="border-t border-gray-700/50"
                      >
                        <td className="py-1.5 pr-4 text-gray-400 align-top w-2/5">
                          <span className="flex items-center gap-1">
                            {prop.displayName}
                            {prop.hidden && (
                              <span className="text-xs text-gray-600" title="Hidden property">
                                (Hidden)
                              </span>
                            )}
                          </span>
                        </td>
                        <td className="py-1.5 text-white align-top">
                          <div className="flex items-start gap-2">
                            <span className="break-all">
                              {formatPropertyValue(prop.displayValue)}
                            </span>
                            {prop.units && (
                              <span className="text-xs text-gray-500 flex-shrink-0">
                                {prop.units}
                              </span>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// Helper function to format property values
function formatPropertyValue(value: any): string {
  if (value === null || value === undefined) return 'N/A';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') {
    // Format numbers with appropriate precision
    if (Number.isInteger(value)) return value.toString();
    return value.toFixed(3);
  }
  return String(value);
}